import React, { useCallback, useState, useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { keysJoin } from 'fulogics-ui-core';
import { connect } from 'react-redux';
import { useMemoDataSource } from 'fulogics-ui-web/dist/layout/hooks/useMemoDataSource';
import DataSource from 'devextreme/data/data_source';
import { formatDate } from 'devextreme/localization';
import enOrderState from '../../../api/enums/enOrderState';
import UserModelSimpleText, {
    UserModelSimpleTextData,
    UserModelSimpleTextDataKeys,
} from 'fulogics-ui-web/dist/layout/components/user-model-simple-text/UserModelSimpleText';
import LogisticUnitSimpleText, {
    LogisticUnitSimpleTextData,
    LogisticUnitSimpleTextDataKeys,
} from 'fulogics-ui-web/dist/layout/components/logistic-unit-simple-text/LogisticUnitSimpleText';
import {
    DriverModelSimpleTextData,
    DriverModelSimpleTextDataKeys,
} from 'fulogics-ui-web/dist/layout/components/driver-model-simple-text/DriverModelSimpleText';
import style from './OrderDetails.module.scss';
import StateOrderSimpleText from '../state-order-simple-text/StateOrderSimpleText';
import DataGrid from 'fulogics-ui-web/dist/layout/dx/DataGrid/DataGrid';
import { Template } from 'devextreme-react/core/template';
import ForwardStepTypeColumnTemplate from '../../templates/dataGrid/column/forward-step-type/ForwardStepTypeColumnTemplate';
import ReturnStepTypeColumnTemplate from '../../templates/dataGrid/column/return-step-type/ReturnStepTypeColumnTemplate';
import ProgressBarBoxes, { RemainingTrailerSpace } from '../progress-bar-boxes/ProgressBarBoxes';
import { orderDetailsForwardGridId, orderDetailsReturnGridId } from './inspectorOptions';
import withInspector from 'fulogics-ui-web/dist/layout/inspector/hoc/withInspector';
import { iRootDispatch, iRootState, select } from '../../../store/store';
import { COSOrderForwardStepStore } from '../../../store/models/COSOrderForwardStepModel';
import { OrderForwardStepItemKeys } from '../order-edit-form/order-forward/OrderForward';
import { COSOrderReturnStepStore } from '../../../store/models/COSOrderReturnStepModel';
import { OrderReturnStepItemKeys } from '../order-edit-form/order-return/OrderReturn';
import OrderDetailSchema from './OrderDetailSchema';
import { Button } from 'devextreme-react/button';
import enArrowDirection from '../../../api/enums/enArrowDirection';
import { useVStore } from 'fulogics-ui-web/dist/layout/hooks/useVStore';
import InspectorVariablesContext from 'fulogics-ui-web/dist/layout/inspector/context/InspectorVariablesContext';
import PayloadDetailsPopup, {
    PayloadDetailsPopupData,
} from './payload-details-popup/PayloadDetailsPopup';
import {
    getRemainingForwardTrailerSpacePayload,
    getRemainingReturnTrailerSpacePayload,
} from '../../../store/models/COSOrdersModel';
import { getTrailerCapacity } from '../progress-bar-boxes/getTrailerCapacity';

const selection = select((_models) => ({}));

const mapStateToProps = (state: iRootState) =>
    ({
        ...(selection(state) as any),
    } as OrderDetailsDisplayOptions);

const mapDispatch = (_dispatch: iRootDispatch, _ownDispatch: OrderDetailsDisplayOptions) =>
    ({
        createForwardStepsCollectionDataSource: (forwardStepsCollectionId?: string) => {
            const opts = COSOrderForwardStepStore.createByCollectionId(forwardStepsCollectionId, {
                defaultOrderByPolicy: 'timetable',
            });
            if (opts) opts.select = OrderForwardStepItemKeys;

            return opts;
        },
        createReturnStepsCollectionDataSource: (returnStepsCollectionId?: string) => {
            const opts = COSOrderReturnStepStore.createByCollectionId(returnStepsCollectionId, {
                defaultOrderByPolicy: 'timetable',
            });
            if (opts) opts.select = OrderReturnStepItemKeys;

            return opts;
        },
        getRemainingForwardTrailerSpace: _dispatch?.COSOrders?.getRemainingForwardTrailerSpace,
        getRemainingReturnTrailerSpace: _dispatch?.COSOrders?.getRemainingReturnTrailerSpace,
        ..._ownDispatch,
    } as OrderDetailsDisplayOptions);

const OrderDetails = connect(
    mapStateToProps,
    mapDispatch as any
)(
    ({
        data,
        className,
        createForwardStepsCollectionDataSource,
        createReturnStepsCollectionDataSource,
        getRemainingForwardTrailerSpace,
        getRemainingReturnTrailerSpace,
        displayCount,
    }: OrderDetailsProps) => {
        const { t } = useTranslation('OrderDetails');
        const [remainingForwardTrailerSpace, setRemainingForwardTrailerSpace] =
            useState<RemainingTrailerSpace | undefined>();
        const [remainingReturnTrailerSpace, setRemainingReturnTrailerSpace] =
            useState<RemainingTrailerSpace | undefined>();
        const orderStepsForwardDataSource = useMemoDataSource(
            () => createForwardStepsCollectionDataSource?.(data?.forwardSteps?.id),
            [data?.forwardSteps?.id]
        );
        const orderStepsReturnDataSource = useMemoDataSource(
            () => createReturnStepsCollectionDataSource?.(data?.returnSteps?.id),
            [data?.returnSteps?.id]
        );

        const [showMoreEditors, setShowMoreEditors] = useState(false);
        const [selectedStep, setSelectedStep] =
            useState<(PayloadDetailsPopupData & { isReturnStep?: boolean }) | undefined>(undefined);
        const count = displayCount ?? 3;
        let displayObj = [];

        if (data?.editedBy) {
            if (showMoreEditors) {
                for (let i = 0; i < data?.editedBy.length; i++) {
                    displayObj.push(
                        <UserModelSimpleText
                            className={style.marginBottom}
                            key={`${data?.editedBy[i].user?.id}-editedBy`}
                            data={data?.editedBy[i].user}
                            showPicture
                        />
                    );
                }
            } else {
                for (let x = 0; x < count && x < data?.editedBy.length; x++) {
                    if (data?.editedBy[x].user != undefined) {
                        displayObj.push(
                            <UserModelSimpleText
                                className={style.marginBottom}
                                key={`${data?.editedBy[x].user?.id}-editedBy`}
                                data={data?.editedBy[x].user}
                                showPicture
                            />
                        );
                    }
                }
            }
        }

        const handleForwardStepTypeClick = useCallback((data?: PayloadDetailsPopupData) => {
            setSelectedStep({ ...data, isReturnStep: false });
        }, []);

        const handleReturnStepTypeClick = useCallback((data?: PayloadDetailsPopupData) => {
            setSelectedStep({ ...data, isReturnStep: true });
        }, []);

        const handlePayloadDetailsHide = useCallback(() => {
            setSelectedStep(undefined);
        }, []);

        const vStore = useVStore(
            () => ({
                variables: {
                    handleForwardStepTypeClick,
                    handleReturnStepTypeClick,
                },
            }),
            [handleForwardStepTypeClick, handleReturnStepTypeClick]
        );

        const showMoreEditorsOnClick = () => {
            setShowMoreEditors(!showMoreEditors);
        };

        if (data?.editedBy && data?.editedBy.length > count && !showMoreEditors) {
            displayObj.push(
                <Button onClick={showMoreEditorsOnClick} stylingMode="text" width={120}>
                    +{data?.editedBy.length - count} {t('morePersons')}
                </Button>
            );
        } else if (data?.editedBy && data?.editedBy.length > count && showMoreEditors) {
            displayObj.push(
                <Button onClick={showMoreEditorsOnClick} stylingMode="text" width={120}>
                    {t('hide')}
                </Button>
            );
        }

        const {
            filledTrailerCapacity: filledForwardTrailerCapacity,
            maxTrailerCapacity: maxForwardTrailerCapacity,
        } = getTrailerCapacity(remainingForwardTrailerSpace);

        const {
            filledTrailerCapacity: filledReturnTrailerCapacity,
            maxTrailerCapacity: maxReturnTrailerCapacity,
        } = getTrailerCapacity(remainingReturnTrailerSpace);

        useEffect(() => {
            if (data?.id) {
                getRemainingForwardTrailerSpace?.({
                    id: data.id,
                }).then(setRemainingForwardTrailerSpace);
                getRemainingReturnTrailerSpace?.({
                    id: data.id,
                }).then(setRemainingReturnTrailerSpace);
            }
        }, [data?.id]);

        return (
            <div className={classNames(className, style.container)}>
                <PayloadDetailsPopup
                    visible={!!selectedStep}
                    data={selectedStep}
                    onHiding={handlePayloadDetailsHide}
                    isReturnStep={selectedStep?.isReturnStep}
                />
                <div className="dx-fieldset">
                    <div className="dx-field">
                        <div className={classNames('dx-field-label', style.labelEnd)}>
                            {t('operator')}
                        </div>
                        <div className="dx-field-value-static">
                            <div className={style.flexWrapper}>
                                <LogisticUnitSimpleText
                                    className={style.marginBottom}
                                    data={data?.transportOperator?.operatingOrganization}
                                />
                            </div>
                            <UserModelSimpleText
                                className={style.marginBottom}
                                showPicture
                                data={data?.transportOperator?.driver}
                            />
                        </div>
                    </div>
                    <div className="dx-field">
                        <div className="dx-field-label">{t('draftCloseDeadline')}</div>
                        <div className={classNames('dx-field-value-static', style.orderDate)}>
                            {data?.draftCloseDeadline
                                ? formatDate(data.draftCloseDeadline, 'HH:mm / dd.MM.yyyy')
                                : '-'}
                        </div>
                    </div>
                    <div className="dx-field">
                        <div className={classNames('dx-field-label', style.labelEnd)}>
                            {t('editors')}
                        </div>
                        <div className="dx-field-value-static">{displayObj}</div>
                    </div>
                    <div className="dx-field">
                        <div className="dx-field-label">{t('status')}</div>
                        <div className="dx-field-value-static">
                            <StateOrderSimpleText
                                className={style.state}
                                data={data}
                                inline
                                statusAsTag
                                userModelSimpleTextDisplayOptions={{
                                    showPicture: true,
                                }}
                                showDeadlineInfo
                            />
                        </div>
                    </div>
                </div>
                <InspectorVariablesContext store={vStore}>
                    <div className={style.gridWrapper}>
                        <label>{t('ordersForwardTitle')}</label>
                        <ProgressBarBoxes
                            name={t('forwardStepsProgressBarTitle')}
                            itemsCount={filledForwardTrailerCapacity}
                            maxItemsCount={maxForwardTrailerCapacity}
                            arrowDirection={enArrowDirection.Right}
                            remainingTrailerSpace={remainingForwardTrailerSpace}
                            hidePayloadDetails
                        />
                        <div className={style.flexGrid}>
                            <DataGrid
                                inspectorId={orderDetailsForwardGridId}
                                keyExpr="id"
                                dataSource={orderStepsForwardDataSource}
                            >
                                <Template
                                    name="ForwardStepTypeColumn"
                                    render={ForwardStepTypeColumnTemplate}
                                />
                            </DataGrid>
                        </div>
                    </div>
                    <div className={style.gridWrapper}>
                        <label>{t('ordersReturnTitle')}</label>
                        <ProgressBarBoxes
                            name={t('returnStepsProgressBarTitle')}
                            itemsCount={filledReturnTrailerCapacity}
                            maxItemsCount={maxReturnTrailerCapacity}
                            arrowDirection={enArrowDirection.Left}
                            remainingTrailerSpace={remainingReturnTrailerSpace}
                            hidePayloadDetails
                        />
                        <div className={style.flexGrid}>
                            <DataGrid
                                inspectorId={orderDetailsReturnGridId}
                                keyExpr="id"
                                dataSource={orderStepsReturnDataSource}
                            >
                                <Template
                                    name="ReturnStepTypeColumn"
                                    render={ReturnStepTypeColumnTemplate}
                                />
                            </DataGrid>
                        </div>
                    </div>
                </InspectorVariablesContext>
            </div>
        );
    }
);

export default withInspector<OrderDetailsProps>(
    OrderDetails,
    OrderDetailSchema,
    undefined,
    undefined,
    { displayCount: 3 }
);

export type OrderDetailsProps = {
    data?: OrderDetailsData;
} & OrderDetailsDisplayOptions;

export type OrderDetailsDisplayOptions = {
    className?: string;
    //
    createForwardStepsCollectionDataSource?: (forwardStepsCollectionId?: string) => DataSource;
    createReturnStepsCollectionDataSource?: (forwardStepsCollectionId?: string) => DataSource;
    //
    getRemainingForwardTrailerSpace?: (
        payload: getRemainingForwardTrailerSpacePayload
    ) => Promise<RemainingTrailerSpace | undefined>;
    getRemainingReturnTrailerSpace?: (
        payload: getRemainingReturnTrailerSpacePayload
    ) => Promise<RemainingTrailerSpace | undefined>;
    //
    displayCount?: number;
};

export type OrderDetailsData = {
    id?: string | number;
    draftCloseDeadline?: Date;
    state?: enOrderState;
    currentEditing?: {
        editingBy?: UserModelSimpleTextData;
    };
    editedBy?: {
        user?: UserModelSimpleTextData;
    }[];
    transportOperator?: {
        operatingOrganization?: LogisticUnitSimpleTextData;
        driver?: UserModelSimpleTextData;
    };
    forwardSteps?: {
        id?: string;
        filledTrailerCapacity?: number;
        maxTrailerCapacity?: number;
    };
    returnSteps?: {
        id?: string;
        filledTrailerCapacity?: number;
        maxTrailerCapacity?: number;
    };
};

export const OrderDetailsDataKeys = [
    'id',
    'draftCloseDeadline',
    ...keysJoin(UserModelSimpleTextDataKeys, 'currentEditing.editingBy.'),
    ...keysJoin(UserModelSimpleTextDataKeys, 'editedBy.user.'),
    ...keysJoin(LogisticUnitSimpleTextDataKeys, 'transportOperator.operatingOrganization.'),
    ...keysJoin(DriverModelSimpleTextDataKeys, 'transportOperator.driver.'),
    'forwardSteps.id',
    'forwardSteps.filledTrailerCapacity',
    'forwardSteps.maxTrailerCapacity',
    'returnSteps.id',
    'returnSteps.filledTrailerCapacity',
    'returnSteps.maxTrailerCapacity',
];
